import Config from 'Config';
import { getCurrentCompanyId, getPermissions } from 'Lib/Utils';
import { getWithoutOverride, httpDelete, patchWithoutOverride, postWithoutOverride } from 'Redux/api';
import { logger } from 'utils/logger';
import { debugInformation } from './errorHelper';

export const v1 = {
    searchBeneficiaries: async (searchTerm, hideNonMatchResults = false) => {
        try {
            const result = await getWithoutOverride(
                `${Config.cards_api}/analytics/search-beneficiaries`,
                {
                    companyId: getCurrentCompanyId(),
                    searchTerm,
                },
                null,
                true
            );

            const applicableRecords = hideNonMatchResults
                ? result?.data?.filter((m) => m._score > 0) || []
                : result?.data || [];

            return {
                success: true,
                data: applicableRecords.map(mapOpenSearchBeneficiary),
            };
        } catch (err) {
            console.error(debugInformation('beneficiaries.searchBeneficiaries'), err);
            return {
                success: false,
                message: err.message,
            };
        }
    },

    getBeneficiaryMatch: async (resourceId) => {
        try {
            const result = await getWithoutOverride(
                `${Config.beneficiaries_api}/matches/${resourceId}`,
                {},
                null,
                true
            );

            return {
                success: true,
                data: result.data,
            };
        } catch (err) {
            console.error(debugInformation('beneficiaries.getBeneficiaryMatch'), err);
            return { succes: false, message: err.message };
        }
    },

    resolveMatch: async (resourceId, createdAt, resolvedFields = {}) => {
        try {
            const result = await patchWithoutOverride(
                `${Config.beneficiaries_api}/matches/${resourceId}`,
                {
                    createdAt,
                    resolved: true,
                    resolvedWith: resolvedFields,
                },
                null,
                true,
                true
            );

            return {
                success: true,
                data: result.data,
            };
        } catch (err) {
            console.error(debugInformation('beneficiaries.resolveMatch'), err);
            return { success: false };
        }
    },

    /**
     *
     * @param {string} BeneficiaryId
     * @param {string} CompanyId
     * @returns {Promise<{data: { BeneficiaryName: null} | import('./beneficiaries/types').Beneficiary, success: true} | { success: false}> }
     */
    getBeneficiary: async (BeneficiaryId, _CompanyId) => {
        if (!getPermissions()?.permissions?.beneficiaries?.view) {
            console.error(
                debugInformation('beneficiaries.getBeneficiary: User does not have permission to view beneficiary')
            );
            return {
                success: false,
            };
        }
        try {
            const res = await v0.getBeneficiary(BeneficiaryId);

            return {
                data: res?.data || { BeneficiaryName: null },
                success: true,
            };
        } catch (error) {
            console.error(debugInformation('beneficiaries.getBeneficiary'), error);
            return { success: false };
        }
    },
};

export const v0 = {
    getBeneficiary: async (BeneficiaryId) => {
        if (!getPermissions()?.permissions?.beneficiaries?.view) {
            console.error(
                debugInformation('beneficiaries.getBeneficiary: User does not have permission to view beneficiary')
            );
            return {
                success: false,
            };
        }
        try {
            const res = await getWithoutOverride(`${Config.beneficiaries_api}/v0/${BeneficiaryId}`, {}, null, true);

            return {
                data: res?.data || { BeneficiaryName: null },
                success: true,
            };
        } catch (error) {
            console.error(debugInformation('beneficiaries.getBeneficiary'), error);
            return { success: false };
        }
    },
    deleteBeneficiary: async (beneficiaryId) => {
        if (!getPermissions()?.permissions?.beneficiaries?.create) {
            console.error(
                debugInformation('beneficiaries.deleteBeneficiary: User does not have permission to modify beneficiary')
            );
            return {
                success: false,
            };
        }

        try {
            await httpDelete(`${Config.beneficiaries_api}/v0/${beneficiaryId}`, {}, null, true);
            return { success: true };
        } catch (error) {
            console.error(debugInformation('beneficiaries.deleteBeneficiary'), error);
            return { success: false };
        }
    },
    createBeneficiary: async (beneficiaryData) => {
        if (!getPermissions()?.permissions?.beneficiaries?.create) {
            logger.error(
                debugInformation('beneficiaries.createBeneficiary: User does not have permission to create beneficiary')
            );
            return {
                success: false,
            };
        }

        try {
            const resp = await postWithoutOverride(`${Config.beneficiaries_api}/v0`, beneficiaryData, null, true);
            return { success: true, data: resp.data, BeneficiaryId: resp.data?.BeneficiaryId ?? '' };
        } catch (error) {
            logger.error(debugInformation('beneficiaries.createBeneficiary'), error);
            return { success: false };
        }
    },
    updateBeneficiary: async ({ beneficiaryId, beneficiaryData }) => {
        if (!getPermissions()?.permissions?.beneficiaries?.create) {
            logger.error(
                debugInformation('beneficiaries.updateBeneficiary: User does not have permission to update beneficiary')
            );
            return {
                success: false,
            };
        }

        try {
            const resp = await patchWithoutOverride(
                `${Config.beneficiaries_api}/v0/${beneficiaryId}`,
                beneficiaryData,
                null,
                true
            );
            return { success: true, data: resp.data, BeneficiaryId: resp.data?.BeneficiaryId ?? '' };
        } catch (error) {
            logger.error(debugInformation('beneficiaries.updateBeneficiary'), error);
            return { success: false, error: error.response?.data?.detail ?? 'Failed to update beneficiary.' };
        }
    },
};

const mapOpenSearchBeneficiary = (openSearchBeneficiaryResult) => {
    return {
        BeneficiaryId: openSearchBeneficiaryResult._id,
        CompanyId: openSearchBeneficiaryResult._source.companyId,
        ABN: openSearchBeneficiaryResult._source.abn,
        Active: openSearchBeneficiaryResult._source.active,
        AccountNumber: openSearchBeneficiaryResult._source.accountNumber,
        Address: openSearchBeneficiaryResult._source.address,
        BankAccountHolderName: openSearchBeneficiaryResult._source.bankAccountHolderName,
        BankCode: openSearchBeneficiaryResult._source.bankCode,
        BankCodeType: openSearchBeneficiaryResult._source.bankCodeType,
        BankCodeValue: openSearchBeneficiaryResult._source.bankCodeValue,
        BankCountry: openSearchBeneficiaryResult._source.bankCountry,
        BeneficiaryCountry: openSearchBeneficiaryResult._source.beneficiaryCountry,
        BeneficiaryFirstName: openSearchBeneficiaryResult._source.beneficiaryFirstName,
        BeneficiaryLastName: openSearchBeneficiaryResult._source.beneficiaryLastName,
        BeneficiaryName: openSearchBeneficiaryResult._source.beneficiaryName,
        BICSWIFT: openSearchBeneficiaryResult._source.bicSwift,
        City: openSearchBeneficiaryResult._source.city,
        CompanyName: openSearchBeneficiaryResult._source.companyName,
        Country: openSearchBeneficiaryResult._source.country,
        Currency: openSearchBeneficiaryResult._source.currency,
        CustomerReferenceNumber: openSearchBeneficiaryResult._source.customerReferenceNumber,
        Email: openSearchBeneficiaryResult._source.email,
        EntityType: openSearchBeneficiaryResult._source.entityType,
        IBAN: openSearchBeneficiaryResult._source.iban,
        PayIdType: openSearchBeneficiaryResult._source.payIdType,
        PaymentType: openSearchBeneficiaryResult._source.paymentType,
        PostCode: openSearchBeneficiaryResult._source.postcode,
        ReferenceId: openSearchBeneficiaryResult._source.referenceId,
        State: openSearchBeneficiaryResult._source.state,
        XeroContactId: openSearchBeneficiaryResult._source.xeroContactId,
        XeroPreferredAccountCode: openSearchBeneficiaryResult._source.xeroPreferredAccountCode,
        Timestamp: new Date(openSearchBeneficiaryResult._source.timestamp).valueOf() / 1000,
        Updated: new Date(openSearchBeneficiaryResult._source.updated).valueOf() / 1000,
        Created: new Date(openSearchBeneficiaryResult._source.created).valueOf() / 1000,
    };
};
